// =====================================================
// Tour Request Form - Modal form for tour requests
// Submits to bh_chatbot_analytics.lead_submissions
// =====================================================

import { useState } from 'react';
import { submitTourRequest } from '../services/api';
import { useChatStore } from '../store/chatStore';

/**
 * Format phone number to US format: (XXX) XXX-XXXX as user types
 */
function formatPhoneNumber(value: string): string {
  // Remove all non-numeric characters
  const digits = value.replace(/\D/g, '');

  // Limit to 10 digits
  const limited = digits.slice(0, 10);

  // Format based on length
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
}

interface TourRequestFormProps {
  isOpen: boolean;
  onClose: () => void;
  prefillData?: {
    cityOfInterest?: string;
    communityOfInterest?: string;
    addressOrFloorPlan?: string;
    // Separate fields for home address and floor plan name
    homeAddress?: string;
    floorPlanName?: string;
    // Fields for tracking inquiry source
    inquiryType?: 'quick_move_in' | 'floor_plan' | 'community' | 'general';
    homeId?: number;
    floorPlanId?: number;
    communityId?: number;
    regionId?: number;
  };
  onSuccess?: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  cityOfInterest: string;
  communityOfInterest: string;
  addressOrFloorPlan: string;
  comments: string;
}

const TourRequestForm: React.FC<TourRequestFormProps> = ({
  isOpen,
  onClose,
  prefillData,
  onSuccess
}) => {
  const { sessionId, markHomeSubmitted, markFloorPlanSubmitted } = useChatStore();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    cityOfInterest: prefillData?.cityOfInterest || '',
    communityOfInterest: prefillData?.communityOfInterest || '',
    addressOrFloorPlan: prefillData?.addressOrFloorPlan || '',
    comments: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Format phone number as user types
    const formattedValue = name === 'phone' ? formatPhoneNumber(value) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue
    }));
    // Clear error when user starts typing
    if (submitError) setSubmitError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Submit to backend API
      const response = await submitTourRequest({
        session_id: sessionId || `session_${Date.now()}`,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        city_of_interest: formData.cityOfInterest || undefined,
        community_of_interest: formData.communityOfInterest || undefined,
        address_or_floor_plan: formData.addressOrFloorPlan || undefined,
        // Pass home address and floor plan name separately
        home_address: prefillData?.homeAddress,
        floor_plan_name: prefillData?.floorPlanName,
        comments: formData.comments || undefined,
        lead_type: 'tour_request',
        source_url: window.location.href,
        // Pass inquiry source tracking fields
        inquiry_type: prefillData?.inquiryType || 'general',
        home_id: prefillData?.homeId,
        floor_plan_id: prefillData?.floorPlanId,
        community_id: prefillData?.communityId,
        region_id: prefillData?.regionId
      });

      if (response.success) {
        setSubmitSuccess(true);

        // Mark the home or floor plan as submitted to prevent duplicate requests
        if (prefillData?.inquiryType === 'quick_move_in' && prefillData?.homeId) {
          markHomeSubmitted(prefillData.homeId);
        } else if (prefillData?.inquiryType === 'floor_plan' && prefillData?.floorPlanId) {
          markFloorPlanSubmitted(prefillData.floorPlanId);
        }

        // Show success message for 4 seconds before closing
        setTimeout(() => {
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            cityOfInterest: prefillData?.cityOfInterest || '',
            communityOfInterest: prefillData?.communityOfInterest || '',
            addressOrFloorPlan: prefillData?.addressOrFloorPlan || '',
            comments: ''
          });
          setSubmitSuccess(false);
          onClose();
          if (onSuccess) onSuccess();
        }, 4000);
      } else {
        setSubmitError(response.message || 'Failed to submit request. Please try again.');
      }
    } catch (error: any) {
      console.error('Tour request submission error:', error);
      setSubmitError(
        error.response?.data?.message ||
        'Unable to submit your request. Please try again later.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Success state
  if (submitSuccess) {
    return (
      <div className="chat-card animate-slide-up">
        <div className="bg-green-600 text-white px-4 py-3 rounded-t-xl">
          <h2 className="text-base font-semibold">Request Submitted!</h2>
        </div>
        <div className="p-6 text-center">
          <svg
            className="w-16 h-16 mx-auto text-green-500 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-gray-700 text-sm">
            Thank you! A specialist will contact you within 24 hours.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-card animate-slide-up">
      {/* Header */}
      <div className="bg-betenbough-olive text-white px-4 py-3 rounded-t-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Request a Tour</h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Close form"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {submitError && (
        <div className="mx-4 mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-xs">{submitError}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-4 space-y-3">
        {/* First Name & Last Name Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="firstName"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2
                         focus:ring-betenbough-olive focus:border-betenbough-olive
                         outline-none transition-all text-sm"
              placeholder="John"
            />
          </div>
          <div>
            <label
              htmlFor="lastName"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2
                         focus:ring-betenbough-olive focus:border-betenbough-olive
                         outline-none transition-all text-sm"
              placeholder="Doe"
            />
          </div>
        </div>

        {/* Email & Phone Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="email"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2
                         focus:ring-betenbough-olive focus:border-betenbough-olive
                         outline-none transition-all text-sm"
              placeholder="email@example.com"
            />
          </div>
          <div>
            <label
              htmlFor="phone"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2
                         focus:ring-betenbough-olive focus:border-betenbough-olive
                         outline-none transition-all text-sm"
              placeholder="(555) 123-4567"
            />
          </div>
        </div>

        {/* City & Community Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="cityOfInterest"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              City of Interest
            </label>
            <input
              type="text"
              id="cityOfInterest"
              name="cityOfInterest"
              value={formData.cityOfInterest}
              onChange={handleChange}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2
                         focus:ring-betenbough-olive focus:border-betenbough-olive
                         outline-none transition-all text-sm"
              placeholder="Lubbock"
            />
          </div>
          <div>
            <label
              htmlFor="communityOfInterest"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Community of Interest
            </label>
            <input
              type="text"
              id="communityOfInterest"
              name="communityOfInterest"
              value={formData.communityOfInterest}
              onChange={handleChange}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2
                         focus:ring-betenbough-olive focus:border-betenbough-olive
                         outline-none transition-all text-sm"
              placeholder="Bell Farms"
            />
          </div>
        </div>

        {/* Address or Floor Plan of Interest */}
        <div>
          <label
            htmlFor="addressOrFloorPlan"
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            Address or Floor Plan of Interest
          </label>
          <input
            type="text"
            id="addressOrFloorPlan"
            name="addressOrFloorPlan"
            value={formData.addressOrFloorPlan}
            onChange={handleChange}
            className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2
                       focus:ring-betenbough-olive focus:border-betenbough-olive
                       outline-none transition-all text-sm"
            placeholder="14408 Paris Ave or Victoria Floor Plan"
          />
        </div>

        {/* Comments or Questions */}
        <div>
          <label
            htmlFor="comments"
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            Comments or Questions
          </label>
          <textarea
            id="comments"
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            rows={2}
            className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2
                       focus:ring-betenbough-olive focus:border-betenbough-olive
                       outline-none transition-all text-sm resize-none"
            placeholder="Any questions or specific requests..."
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-white hover:bg-gray-50 text-betenbough-olive border
                       border-betenbough-olive py-2 rounded-lg transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-betenbough-olive hover:bg-betenbough-olive/90 text-white
                       py-2 rounded-lg transition-colors text-sm font-medium disabled:opacity-50
                       disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TourRequestForm;
